import { Country } from 'country-state-city';

const getValidCountryCodes = (): Set<string> => {
	const validCodes = new Set<string>();
	try {
		const allCountries = Country.getAllCountries();
		if (allCountries && Array.isArray(allCountries)) {
			allCountries.forEach((country: any) => {
				if (country.phonecode) {
					validCodes.add(country.phonecode.toString());
				}
			});
		}
	} catch {
		console.warn('Could not load country codes from country-state-city');
	}
	return validCodes;
};

const VALID_COUNTRY_CODES = getValidCountryCodes();

export const getPhoneValidationError = (
	value: string
):
	| 'invalid-format'
	| 'invalid-country-code'
	| 'invalid-phone-length'
	| null => {
	if (!value || typeof value !== 'string') {
		return 'invalid-format';
	}

	const trimmed = value.trim();

	if (!trimmed.startsWith('+')) {
		return 'invalid-format';
	}

	const digitsOnly = trimmed.replace(/[^\d+]/g, '');
	const phoneDigits = digitsOnly.substring(1);

	if (!phoneDigits || phoneDigits.length < 7) {
		return 'invalid-phone-length';
	}

	if (phoneDigits.length > 15) {
		return 'invalid-phone-length';
	}

	const countryCode1 = phoneDigits.substring(0, 1);
	const countryCode2 = phoneDigits.substring(0, 2);
	const countryCode3 = phoneDigits.substring(0, 3);

	const hasValidCountryCode =
		VALID_COUNTRY_CODES.has(countryCode3) ||
		VALID_COUNTRY_CODES.has(countryCode2) ||
		VALID_COUNTRY_CODES.has(countryCode1);

	if (!hasValidCountryCode) {
		return 'invalid-country-code';
	}

	if (VALID_COUNTRY_CODES.has(countryCode3) && phoneDigits.length >= 10) {
		return null;
	}
	if (VALID_COUNTRY_CODES.has(countryCode2) && phoneDigits.length >= 9) {
		return null;
	}
	if (VALID_COUNTRY_CODES.has(countryCode1) && phoneDigits.length >= 10) {
		return null;
	}

	return 'invalid-phone-length';
};

export const validatePhoneNumber = (value: string): boolean => {
	return getPhoneValidationError(value) === null;
};

type ValidatorKey = 'email' | 'web' | 'aftn' | 'sita' | 'address';

const regexValidators: Record<ValidatorKey, () => RegExp> = {
	email: () => /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/gi,
	web: () =>
		/(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/gi,
	aftn: () => /^[A-Z]{8}$/,
	sita: () => /^[A-Z, 0-9]{7}$/,
	address: () => /([A-Z]|[0-9])/gi,
};

export const validators: Record<string, null | (() => RegExp)> = {
	phone: null,
	fax: null,
	...regexValidators,
};

export const validationErrors: Record<string, string> = {
	phone: 'Please enter a valid phone number',
	'phone-invalid-format': 'Phone number must start with +',
	'phone-invalid-country-code': 'Invalid country code',
	'phone-invalid-phone-length':
		'Phone number looks incomplete. Please include your full number after the country code',
	fax: 'Please enter a valid fax number',
	'fax-invalid-format': 'Fax number must start with +',
	'fax-invalid-country-code': 'Invalid country code',
	'fax-invalid-phone-length': 'Fax number looks incomplete. Please include your full number after the country code',
	email: 'Please enter a valid email address.',
	web: 'Invalid website URL.',
	aftn: 'AFTN should include 8 letters',
	sita: 'SITA should include 7 letters or numbers',
	address: 'Address should include latin letters and numbers.',
};

export const validate = (value: string, type: string): boolean => {
	if (type === 'phone' || type === 'fax') {
		return validatePhoneNumber(value);
	}

	if (!(type in regexValidators)) {
		return true;
	}

	const regex = regexValidators[type as ValidatorKey]();
	return regex.test(value ?? '');
};
